import { Pipe, PipeTransform, inject } from '@angular/core';
import { from, map } from 'rxjs';

import type {
  UserDetailsPipeReturn,
  ValidSearchTypes,
} from '../../../private/utils/types.private';
import { UserDetailsService } from '../../services';

/** Fetch data for a single or multiple entities simply and directly in the component template,
 * without the need to call `UserDetailsService` manually.

If empty strings or arrays are supplied, return objects are governed by `UserDetailsService` handling:

- Empty string results in a `MissingEntity` entity response object
- Empty array results in a an empty array response.
Examples:
```html
<!-- Returning single entity data -->
<ng-container *ngIf="('someId' | userDetails: 'user' | async) as user">
  <p>{{user.name}}</p>
</ng-container>

<!-- Returning multiple entities -->
<ng-container *ngIf="(['firstId', 'secondId', ...string] | userDetails | async) as users">
  <p *ngFor="let user of users">{{user.name}}</p>
</ng-container>
```
*/
@Pipe({
  name: 'userDetails',
  standalone: false,
})
export class UserDetailsPipe implements PipeTransform {
  private readonly userDetailsService = inject(UserDetailsService);

  transform<T extends string | string[], G extends ValidSearchTypes>(
    entityIds: T,
    searchType: G = 'all' as G,
  ): UserDetailsPipeReturn<T, G> {
    if (Array.isArray(entityIds)) {
      return from(
        this.userDetailsService.findEntity(entityIds, searchType),
      ) as UserDetailsPipeReturn<T, G>;
    }

    return from(this.userDetailsService.findEntity(entityIds, searchType)).pipe(
      map((entityData) => entityData[0]),
    ) as UserDetailsPipeReturn<T, G>;
  }
}
